import React, { useState } from 'react';
import { Button, Card, CardBody, InlineAlert, TextField } from '../../ui';

type FieldName = 'apiUsername' | 'apiPassword' | 'apiKey' | 'clientId';
const FIELDS: { name: FieldName; label: string; placeholder: string; type: string; autoComplete: string }[] = [
  { name: 'apiUsername', label: 'API username', placeholder: 'Enter the username', type: 'text', autoComplete: 'off' },
  { name: 'apiPassword', label: 'API password', placeholder: 'Enter the password', type: 'password', autoComplete: 'new-password' },
  { name: 'apiKey', label: 'API key', placeholder: 'Enter the API key', type: 'text', autoComplete: 'off' },
  { name: 'clientId', label: 'Client ID', placeholder: 'Enter the client ID', type: 'text', autoComplete: 'off' },
];

/**
 * Ported from the legacy screen, which validated the four fields and submitted nothing.
 * Saving is deliberately NOT wired: the existing backend endpoint (POST /api/create) stores the
 * password in plain text and the new backend's contract is unknown. Once an endpoint exists,
 * add a service in src/api/services and call it from `submit`.
 */
export function ApiSettingsPage() {
  const [values, setValues] = useState<Record<FieldName, string>>({ apiUsername: '', apiPassword: '', apiKey: '', clientId: '' });
  const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [validated, setValidated] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next: Partial<Record<FieldName, string>> = {};
    FIELDS.forEach((f) => { if (!values[f.name].trim()) next[f.name] = `Enter the ${f.label.toLowerCase()}.`; });
    setErrors(next);
    setValidated(Object.keys(next).length === 0);
  };

  const change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as FieldName;
    setValues((v) => ({ ...v, [name]: e.target.value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
    setValidated(false);
  };

  return (
    <div className="mx-auto max-w-xl">
      <h1 className="text-2xl font-bold tracking-tight text-ink">API settings</h1>
      <p className="mb-5 mt-1 text-sm text-muted">Credentials used to connect external systems.</p>
      <Card>
        <CardBody>
          <form onSubmit={submit} noValidate className="space-y-4">
            {FIELDS.map((f) => (
              <TextField key={f.name} label={f.label} required name={f.name} type={f.type} placeholder={f.placeholder} autoComplete={f.autoComplete} value={values[f.name]} onChange={change} error={errors[f.name]} />
            ))}
            {validated && <InlineAlert tone="warn">Saving is not connected to the backend yet, so nothing was stored.</InlineAlert>}
            <Button type="submit">Submit</Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
